import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, minsToHhMm, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import { fetchCompanyPodsDashboard } from './companyPodsApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  BigStatPanel,
  ProgressList,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  AREA_FILL,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID,
  CHART_GRID_NO_X,
  paletteAt
} from '../../../../component/common/designSystem';
import { SESSION_LENGTH_RAMP, ENGAGEMENT_RAMP, FORMAT_RAMP } from './podsTheme';

/**
 * Shoorah Pods → Dashboard.
 *
 * Everything here comes from the play ledger, so it answers one question in
 * layers: are your people listening (reach, plays, time), how deeply (session
 * length, plays per listener), when (trend, weekday) and to what (top pods,
 * format, experts).
 *
 * The pods catalogue itself is a Shoorah-admin surface — a company sees only
 * what its own people played — so there are deliberately no library panels
 * here, the same call the company Breathwork audio page makes.
 *
 * `fetcher` is a prop so a future super-admin Pods Hub can render this page
 * unscoped; the builders behind both are the same.
 */
const PodsDashboard = ({ fetcher = fetchCompanyPodsDashboard, populationLabel = 'users' }) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  const mostPlayed = stats?.mostPlayed || [];
  const playsTrend = stats?.playsTrend || [];
  const listenMinsTrend = stats?.listenMinsTrend || [];
  const weekdayPlays = stats?.weekdayPlays || [];
  const sessionLengths = stats?.sessionLengthDistribution || [];
  const engagement = stats?.listenerEngagement || [];
  const formatSplit = stats?.formatSplit || [];
  const topExperts = stats?.topExperts || [];

  const topTenPlays = mostPlayed.reduce((sum, item) => sum + (item.plays || 0), 0);

  // What share of the population ever played a pod.
  const reachPercentage =
    stats?.totalUsers > 0 ? Math.round((stats.listeners / stats.totalUsers) * 100) : null;

  // Colour-coded per pod so the bar chart and the breakdown table below read
  // as the same ranking.
  const barOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: mostPlayed.map((item, index) => paletteAt(index)),
    plotOptions: {
      bar: { horizontal: true, borderRadius: 4, barHeight: '65%', distributed: true }
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => formatBigNumber(val),
      style: { fontSize: '12px' }
    },
    legend: { show: false },
    grid: CHART_GRID_NO_X,
    xaxis: {
      categories: mostPlayed.map((item) => item.name),
      labels: { formatter: (val) => formatBigNumber(val) }
    },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} plays` } }
  };

  /**
   * Plays and listening minutes on one chart, two axes: a spike in plays that
   * isn't matched by minutes means people are sampling and dropping out, which
   * is the opposite conclusion from the plays line alone.
   */
  const trendOptions = {
    chart: { ...CHART_BASE, type: 'line' },
    colors: [CHART_COLORS.brand, CHART_COLORS.teal],
    stroke: { curve: 'smooth', width: [2, 2], dashArray: [0, 4] },
    fill: { type: ['gradient', 'solid'], gradient: AREA_FILL.gradient },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(playsTrend.length), tickAmount: 6 },
    yaxis: [
      { seriesName: 'Plays', labels: { formatter: (val) => formatBigNumber(val) } },
      {
        seriesName: 'Listening time',
        opposite: true,
        labels: { formatter: (val) => minsToHhMm(val) }
      }
    ],
    tooltip: {
      shared: true,
      y: [
        { formatter: (val) => `${val.toLocaleString()} plays` },
        { formatter: (val) => minsToHhMm(val) }
      ]
    }
  };

  const weekdayOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.brand],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: weekdayPlays.map((day) => day.label) },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} plays` } }
  };

  return (
    <div>
      {loader ? <Loader /> : null}

      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.catalog}
          title="Most Played Pod"
          value={stats?.mostPlayedPod || '-'}
        />
        <StatCard
          icon={STAT_ICONS.plays}
          title="Total Plays"
          value={stats ? formatBigNumber(stats.totalPlays) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.time}
          title="Total Listening Time"
          value={stats ? minsToHhMm(stats.totalPlayTimeMins) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.users}
          title="Listeners"
          value={stats ? formatBigNumber(stats.listeners) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Plays per Listener"
          value={stats ? stats.avgPlaysPerListener : '-'}
        />
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Avg Session Length"
          value={stats ? `${stats.avgSessionMins} min` : '-'}
        />
      </div>

      {/* ---------- Breakdowns ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="Session Length"
          subtitle="Minutes per play"
          data={sessionLengths}
          colors={SESSION_LENGTH_RAMP}
          unit="plays"
        />
        <MiniDonutCard
          title="Listener Depth"
          subtitle="Plays per listener"
          data={engagement}
          colors={ENGAGEMENT_RAMP}
          unit="listeners"
        />
        <MiniDonutCard
          title="Format"
          subtitle="Audio vs video pods"
          data={formatSplit}
          colors={FORMAT_RAMP}
          unit="plays"
          legendLimit={3}
        />
      </div>

      {/* ---------- Trends ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Most Played Pods" subtitle={`Top 10 among your ${populationLabel}`}>
          {mostPlayed.length > 0 ? (
            <Chart
              type="bar"
              height={380}
              width="100%"
              series={[{ name: 'Plays', data: mostPlayed.map((item) => item.plays) }]}
              options={barOptions}
            />
          ) : (
            <PanelEmpty>No pod activity yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Plays & Listening Time" subtitle="Per day — last 30 days">
          {playsTrend.length > 0 ? (
            <Chart
              type="line"
              height={380}
              width="100%"
              series={[
                { name: 'Plays', type: 'area', data: playsTrend },
                { name: 'Listening time', type: 'line', data: listenMinsTrend }
              ]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>No plays recorded yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Reach ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <BigStatPanel
          title="Listening Reach"
          subtitle={`Share of ${populationLabel} who played a pod`}
          value={reachPercentage != null ? `${reachPercentage}%` : '-'}
          caption={
            stats
              ? `${formatBigNumber(stats.listeners)} of ${formatBigNumber(
                  stats.totalUsers
                )} ${populationLabel}`
              : null
          }
        />

        <Panel title="Plays by Weekday" subtitle="When people reach for a pod">
          {weekdayPlays.some((day) => day.plays > 0) ? (
            <Chart
              type="bar"
              height={280}
              width="100%"
              series={[{ name: 'Plays', data: weekdayPlays.map((day) => day.plays) }]}
              options={weekdayOptions}
            />
          ) : (
            <PanelEmpty>No plays recorded yet.</PanelEmpty>
          )}
        </Panel>

        {/* Expert-authored pods carry a name; Shoorah's own content is grouped
            under "Shoorah", so this reads as "who your people listen to". */}
        <Panel title="Most Listened Experts" subtitle="Plays by pod author">
          <ProgressList items={topExperts} unit="plays" />
        </Panel>
      </div>

      {mostPlayed.length > 0 && (
        <Panel
          title="Top Pods Breakdown"
          subtitle={
            stats?.topTenSharePercentage
              ? `These ${mostPlayed.length} account for ${stats.topTenSharePercentage}% of all plays`
              : 'Plays, listening time and reach per pod'
          }
          className="mt-6"
        >
          <PanelTable
            head={
              <>
                <th className="py-2 pr-4 font-medium">#</th>
                <th className="py-2 pr-4 font-medium">Pod</th>
                <th className="py-2 pr-4 font-medium">Expert</th>
                <th className="py-2 pr-4 font-medium text-right">Plays</th>
                <th className="py-2 pr-4 font-medium text-right">Share of top 10</th>
                <th className="py-2 pr-4 font-medium text-right">Listening Time</th>
                <th className="py-2 font-medium text-right">Listeners</th>
              </>
            }
          >
            {mostPlayed.map((item, index) => (
              <tr key={item.id || index} className={PANEL_ROW_CLASS}>
                <td className="py-2 pr-4">
                  {/* Rank swatch ties each row to its bar in the chart above. */}
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: paletteAt(index) }}
                    />
                    {index + 1}
                  </span>
                </td>
                <td className="py-2 pr-4">{item.name}</td>
                <td className="py-2 pr-4">{item.expert || 'Shoorah'}</td>
                <td className="py-2 pr-4 text-right">{item.plays.toLocaleString()}</td>
                <td className="py-2 pr-4 text-right">
                  {topTenPlays > 0 ? `${Math.round((item.plays / topTenPlays) * 100)}%` : '-'}
                </td>
                <td className="py-2 pr-4 text-right">{minsToHhMm(item.playTimeMins)}</td>
                <td className="py-2 text-right">{item.listeners ?? '-'}</td>
              </tr>
            ))}
          </PanelTable>
        </Panel>
      )}
    </div>
  );
};

PodsDashboard.propTypes = {
  fetcher: PropTypes.func,
  populationLabel: PropTypes.string
};

export default PodsDashboard;
